import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function AdminSlotsPage() {
    const [slots, setSlots] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ data: "", hora: "09:00", capacidade: 1, ativo: true });
    const [saving, setSaving] = useState(false);

    async function load() {
        try {
            const r = await api.get("/admin/slots");
            setSlots(r.data);
        } catch (e) { toast.error("Erro ao carregar"); }
    }
    useEffect(() => { load(); }, []);

    async function save() {
        if (!form.data || !form.hora) { toast.error("Preencha data e hora."); return; }
        setSaving(true);
        try {
            await api.post("/admin/slots", { ...form, capacidade: Number(form.capacidade) || 1 });
            toast.success("Horário criado.");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        } finally { setSaving(false); }
    }
    async function toggle(s) {
        try {
            await api.put(`/admin/slots/${s.id}`, { ativo: !s.ativo });
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro"); }
    }
    async function del(s) {
        if (!window.confirm("Eliminar este horário?")) return;
        try {
            await api.delete(`/admin/slots/${s.id}`);
            toast.success("Eliminado.");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro"); }
    }

    return (
        <>
            <PageHeader
                eyebrow="Administração"
                title="Disponibilidade"
                description="Configure os horários que ficam disponíveis para marcação pelos clientes."
                testid="slots-header"
                actions={
                    <Button onClick={() => { setForm({ data: "", hora: "09:00", capacidade: 1, ativo: true }); setOpen(true); }} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="new-slot-btn">
                        <Plus className="mr-2 h-4 w-4" /> Novo horário
                    </Button>
                }
            />

            {slots.length === 0 ? (
                <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="slots-empty">
                    <CalendarClock className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
                    <h3 className="font-['Outfit'] text-lg font-semibold text-zinc-900">Sem horários configurados.</h3>
                </div>
            ) : (
                <div className="border border-zinc-200 bg-white" data-testid="slots-table">
                    <div className="grid grid-cols-12 gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                        <div className="col-span-3">Data</div>
                        <div className="col-span-2">Hora</div>
                        <div className="col-span-3">Reservas</div>
                        <div className="col-span-2">Ativo</div>
                        <div className="col-span-2 text-right">Ações</div>
                    </div>
                    {slots.map((s) => {
                        const full = s.reservados >= s.capacidade;
                        return (
                            <div key={s.id} className="grid grid-cols-12 items-center gap-2 border-b border-zinc-100 px-4 py-3 text-sm" data-testid={`slot-row-${s.id}`}>
                                <div className="col-span-3 font-mono text-zinc-800">{s.data}</div>
                                <div className="col-span-2 font-mono text-zinc-800">{s.hora}</div>
                                <div className="col-span-3">
                                    {s.reservados}/{s.capacidade}
                                    {full ? <span className="ml-2 inline-block rounded-sm border border-zinc-300 bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-700">Lotado</span> : null}
                                </div>
                                <div className="col-span-2">
                                    <Switch checked={s.ativo} onCheckedChange={() => toggle(s)} data-testid={`slot-active-${s.id}`} />
                                </div>
                                <div className="col-span-2 flex justify-end">
                                    <Button size="icon" variant="outline" className="h-7 w-7 rounded-sm text-red-600 hover:text-red-700" onClick={() => del(s)} data-testid={`slot-del-${s.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="slot-dialog">
                    <DialogHeader><DialogTitle>Novo horário disponível</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Data</Label>
                            <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-1.5 h-10 rounded-sm" data-testid="slot-data" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Hora</Label>
                            <Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} className="mt-1.5 h-10 rounded-sm" data-testid="slot-hora" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">Capacidade (quantos veículos em simultâneo)</Label>
                            <Input type="number" min="1" value={form.capacidade} onChange={(e) => setForm({ ...form, capacidade: e.target.value })} className="mt-1.5 h-10 rounded-sm" data-testid="slot-cap" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF3B30] hover:bg-[#D63026]" data-testid="slot-save">
                            {saving ? "A guardar..." : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
